import React, { useEffect, useState } from 'react';
import AdminDashboard from 'layouts/AdminDashboard';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import {
  getAdminAssignments,
  createAdminAssignment,
  deleteAdminAssignment,
} from './DocketAdminService';

const PROGRAMME_DISPLAY: Record<string, string> = {
  OPTIMISE_MY_FINANCES: 'Optimise My Finances',
  MORTGAGE_PREPARATION: 'Mortgage Preparation',
  TAX_PREPARATION: 'Tax Preparation',
  HEALTH_RECORDS: 'Health Records',
  MEDICATION_TRACKING: 'Medication Tracking',
  ESTATE_PLANNING: 'Estate Planning',
  CONTRACTS: 'Contracts',
};

const EMPTY_FORM = {
  title: '',
  description: '',
  instructions: '',
  assignmentType: 'DOCUMENT',
  sequenceOrder: 1,
  groupCode: '',
  uploadRequired: false,
  required: true,
};

const DocketAssignmentList = (): JSX.Element => {
  const { programmeCode } = useParams<{ programmeCode: string }>();
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (programmeCode) loadAssignments();
  }, [programmeCode]);

  function loadAssignments() {
    setLoading(true);
    getAdminAssignments(programmeCode!)
      .then((res) => setAssignments(res.data))
      .catch(() => setError('Failed to load assignments.'))
      .finally(() => setLoading(false));
  }

  function handleAdd() {
    setSaving(true);
    createAdminAssignment(programmeCode!, form)
      .then(() => {
        setAddOpen(false);
        setForm({ ...EMPTY_FORM });
        loadAssignments();
      })
      .catch(() => setError('Failed to create assignment.'))
      .finally(() => setSaving(false));
  }

  function handleDeleteConfirm() {
    if (deleteId == null) return;
    deleteAdminAssignment(deleteId)
      .then(() => {
        setDeleteOpen(false);
        setDeleteId(null);
        loadAssignments();
      })
      .catch(() => setError('Failed to delete assignment.'));
  }

  const displayName = PROGRAMME_DISPLAY[programmeCode!] || programmeCode;

  return (
    <Container>
      <AdminDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/admin/docket">
              Docket Programmes
            </Link>
            <Typography color="text.primary">{displayName}</Typography>
          </Breadcrumbs>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={4}
        >
          <Typography variant="h4">{displayName}</Typography>
          <Button variant="contained" onClick={() => setAddOpen(true)}>
            Add Assignment
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <Typography>Loading...</Typography>}

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 750 }}>
            <TableHead sx={{ bgcolor: 'alternate.main' }}>
              <TableRow>
                {[
                  'Order',
                  'Title',
                  'Type',
                  'Group',
                  'Upload Req',
                  'Required',
                  'Actions',
                ].map((h) => (
                  <TableCell key={h}>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      fontWeight={700}
                      sx={{ textTransform: 'uppercase' }}
                    >
                      {h}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((a) => (
                <TableRow key={a.id} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell>{a.sequenceOrder}</TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{a.title}</Typography>
                    {a.description && (
                      <Typography variant="caption" color="text.secondary">
                        {a.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{a.assignmentType}</TableCell>
                  <TableCell>{a.groupCode || '—'}</TableCell>
                  <TableCell>{a.uploadRequired ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{a.required ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(
                          `/admin/docket/programme/${programmeCode}/assignment/${a.id}`,
                        )
                      }
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeleteId(a.id);
                        setDeleteOpen(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Assignment Dialog */}
        <Dialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Assignment</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Title"
                fullWidth
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <TextField
                label="Description"
                multiline
                rows={2}
                fullWidth
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
              <TextField
                label="Instructions"
                multiline
                rows={3}
                fullWidth
                value={form.instructions}
                onChange={(e) =>
                  setForm({ ...form, instructions: e.target.value })
                }
              />
              <TextField
                label="Assignment Type"
                fullWidth
                value={form.assignmentType}
                onChange={(e) =>
                  setForm({ ...form, assignmentType: e.target.value })
                }
              />
              <TextField
                label="Sequence Order"
                type="number"
                fullWidth
                value={form.sequenceOrder}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sequenceOrder: parseInt(e.target.value) || 1,
                  })
                }
              />
              <TextField
                label="Group Code"
                fullWidth
                value={form.groupCode}
                onChange={(e) =>
                  setForm({ ...form, groupCode: e.target.value })
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.uploadRequired}
                    onChange={(e) =>
                      setForm({ ...form, uploadRequired: e.target.checked })
                    }
                  />
                }
                label="Upload Required"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.required}
                    onChange={(e) =>
                      setForm({ ...form, required: e.target.checked })
                    }
                  />
                }
                label="Required"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={saving || !form.title}
            >
              {saving ? 'Saving...' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this assignment?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </AdminDashboard>
    </Container>
  );
};

export default DocketAssignmentList;
